import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import "./PaymentLayer.css";

const FEATURE_FLAGS = [
  ["allow_webhook", "mdi:webhook", "Webhooks"],
  ["allowapi", "mdi:key-variant", "API access"],
  ["allowchatbot", "mdi:robot-outline", "Chatbot"],
  ["allowbulkmsg", "mdi:bullhorn-outline", "Bulk messaging"],
  ["allowschedulemsg", "mdi:calendar-clock", "Scheduled sends"],
  ["allow_multi_instance", "mdi:layers-triple-outline", "Multi instance"],
];

const PaymentLayer = () => {
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, cls = "success") => {
    setToast({ message, class: cls });
    setTimeout(() => setToast(null), 2800);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [plansRes, ordersRes, userRes] = await Promise.all([
        apiCall({ url: "plan/get-all", method: "GET" }),
        apiCall({ url: "plan/my-orders", method: "GET" }),
        apiCall({ url: "user/get-user-by-token", method: "GET" }),
      ]);
      if (plansRes?.success) setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      if (ordersRes?.success) setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      if (userRes?.success) {
        setUser(userRes.data || null);
        if (userRes.data) {
          try {
            localStorage.setItem("user", JSON.stringify(userRes.data));
            let planInstance = 0;
            try {
              const plan =
                typeof userRes.data.plan === "string"
                  ? JSON.parse(userRes.data.plan)
                  : userRes.data.plan;
              planInstance = Number(plan?.instance) || 0;
            } catch {
              planInstance = 0;
            }
            const limit = Math.max(
              0,
              Number(userRes.data.allow_multi_instance) || planInstance || 0
            );
            localStorage.setItem("instanceLimit", String(limit));
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err) {
      showToast(err.message || "Failed to load", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Plans & Orders";
    load();
  }, []);

  const buyPlan = async (plan) => {
    setBusyId(plan.id);
    try {
      if (Number(plan.price) <= 0) {
        const res = await apiCall({
          url: "plan/pay-free",
          method: "POST",
          body: { plan_id: plan.id },
        });
        if (res?.success) {
          showToast(res.msg || "Plan activated");
          load();
        } else showToast(res?.msg || "Could not activate", "danger");
        return;
      }

      try {
        const stripeRes = await apiCall({
          url: "plan/create_stripe_session",
          method: "POST",
          body: { planId: plan.id },
        });
        const checkoutUrl =
          stripeRes?.session?.url ||
          stripeRes?.url ||
          stripeRes?.sessionUrl ||
          stripeRes?.checkout_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
      } catch {
        /* fall through to offline order */
      }

      const res = await apiCall({
        url: "plan/request-order",
        method: "POST",
        body: { plan_id: plan.id },
      });
      if (res?.success) {
        showToast(res.msg || "Order placed");
        load();
      } else showToast(res?.msg || "Could not place order", "danger");
    } catch (err) {
      showToast(err.message || "Order failed", "danger");
    } finally {
      setBusyId(null);
    }
  };

  const isFree = Number(user?.is_free) === 1;
  let planName = "No plan";
  let activePlanId = null;
  try {
    const p = typeof user?.plan === "string" ? JSON.parse(user.plan) : user?.plan;
    if (p?.name) planName = p.name;
    if (p?.id != null) activePlanId = p.id;
  } catch {
    /* ignore */
  }

  const featuredId = useMemo(() => {
    if (!plans.length) return null;
    const paid = plans.filter((p) => Number(p.price) > 0);
    if (paid.length >= 2) return paid[Math.floor((paid.length - 1) / 2)].id;
    if (paid.length === 1) return paid[0].id;
    return plans[0].id;
  }, [plans]);

  if (!loading && user && isFree) {
    return <Navigate to="/dashboard" replace />;
  }

  const expiresLabel = user?.planexpire
    ? new Date(user.planexpire).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

  return (
    <div className="plans-page">
      {toast ? (
        <div className={`plans-toast is-${toast.class === "danger" ? "danger" : "success"}`}>
          <Icon
            icon={toast.class === "danger" ? "mdi:alert-circle-outline" : "mdi:check-circle-outline"}
            width={20}
          />
          {toast.message}
        </div>
      ) : null}

      <header className="plans-hero">
        <div className="plans-hero-copy">
          <p className="plans-eyebrow">Workspace plan</p>
          <h1>Plans &amp; orders</h1>
          <p>
            Choose a package to unlock sessions, message credits, chatbot, campaigns, and API
            access for your workspace.
          </p>
        </div>
        <div className="plans-hero-badge">
          <Icon icon="mdi:package-variant-closed" width={22} />
          <div>
            <span>Current plan</span>
            <strong>{planName}</strong>
          </div>
        </div>
      </header>

      {user ? (
        <section className="plans-status">
          <div className="plans-stat">
            <Icon icon="mdi:shield-check-outline" width={20} />
            <div>
              <span>Active package</span>
              <strong>{planName}</strong>
            </div>
          </div>
          <div className="plans-stat">
            <Icon icon="mdi:calendar-end" width={20} />
            <div>
              <span>Expires</span>
              <strong>{expiresLabel}</strong>
            </div>
          </div>
          <div className="plans-stat">
            <Icon icon="mdi:message-text-outline" width={20} />
            <div>
              <span>Messages left</span>
              <strong>{Number(user.msglimit || 0).toLocaleString()}</strong>
            </div>
          </div>
          <div className="plans-stat">
            <Icon icon="mdi:account-box-outline" width={20} />
            <div>
              <span>Contacts left</span>
              <strong>{Number(user.contactlimit || 0).toLocaleString()}</strong>
            </div>
          </div>
        </section>
      ) : null}

      <div className="plans-section-head">
        <h2>Available plans</h2>
        <p>Activate instantly for free tiers, or submit a paid order for confirmation.</p>
      </div>

      {loading ? (
        <p className="plans-empty">Loading plans…</p>
      ) : plans.length === 0 ? (
        <p className="plans-empty">No plans published yet. Check back soon.</p>
      ) : (
        <div className={`plans-grid plans-grid-${Math.min(plans.length, 3)}`}>
          {plans.map((plan, index) => {
            const free = Number(plan.price) <= 0;
            const featured = plan.id === featuredId;
            const current = activePlanId != null && String(activePlanId) === String(plan.id);
            return (
              <article
                key={plan.id}
                className={[
                  "plans-card",
                  free ? "is-free" : "",
                  featured ? "is-featured" : "",
                  current ? "is-current" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ animationDelay: `${index * 70}ms` }}
              >
                {featured ? <span className="plans-badge">Most popular</span> : null}
                {free && !featured ? <span className="plans-badge is-free">Free start</span> : null}
                {current ? <span className="plans-badge is-current">Current</span> : null}

                <div className="plans-card-top">
                  <h3>{plan.name}</h3>
                  <p className="plans-amount">
                    {free ? (
                      "Free"
                    ) : (
                      <>
                        <span className="plans-currency">$</span>
                        {Number(plan.price).toLocaleString()}
                      </>
                    )}
                  </p>
                  <p className="plans-term">{plan.planexpire || "—"} days access</p>
                </div>

                <ul className="plans-limits">
                  <li>
                    <Icon icon="mdi:whatsapp" width={18} />
                    <span>
                      <strong>{plan.instance}</strong> session
                      {Number(plan.instance) === 1 ? "" : "s"}
                    </span>
                  </li>
                  <li>
                    <Icon icon="mdi:message-text-outline" width={18} />
                    <span>
                      <strong>{Number(plan.msglimit || 0).toLocaleString()}</strong> messages
                    </span>
                  </li>
                  <li>
                    <Icon icon="mdi:account-box-outline" width={18} />
                    <span>
                      <strong>{Number(plan.contactlimit || 0).toLocaleString()}</strong> contacts
                    </span>
                  </li>
                  <li>
                    <Icon icon="mdi:file-document-outline" width={18} />
                    <span>
                      <strong>{Number(plan.templetlimit || 0).toLocaleString()}</strong> templates
                    </span>
                  </li>
                </ul>

                <ul className="plans-flags">
                  {FEATURE_FLAGS.map(([key, icon, label]) => {
                    const on = Number(plan[key]) === 1;
                    return (
                      <li key={key} className={on ? "is-on" : "is-off"}>
                        <Icon
                          icon={on ? "mdi:check-circle" : "mdi:close-circle-outline"}
                          width={18}
                        />
                        <Icon icon={icon} width={15} className="plans-flag-icon" />
                        {label}
                      </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  className={`plans-cta${featured ? " is-featured" : ""}`}
                  disabled={busyId === plan.id || current}
                  onClick={() => buyPlan(plan)}
                >
                  {busyId === plan.id
                    ? "Please wait…"
                    : current
                      ? "Active plan"
                      : free
                        ? "Activate free plan"
                        : "Order this plan"}
                  {!current && busyId !== plan.id ? (
                    <Icon icon="mdi:arrow-right" width={18} />
                  ) : null}
                </button>
              </article>
            );
          })}
        </div>
      )}

      <div className="plans-section-head plans-orders-head">
        <h2>Your orders</h2>
        <p>Pending paid orders activate after payment confirmation.</p>
      </div>

      <div className="plans-orders">
        {orders.length === 0 ? (
          <div className="plans-orders-empty">
            <Icon icon="mdi:receipt-text-outline" width={28} />
            <p>No orders yet. Choose a plan above to get started.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="plans-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Mode</th>
                  <th>Amount</th>
                  <th>Details</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>#{o.id}</td>
                    <td>
                      <span className="plans-mode">{o.payment_mode}</span>
                    </td>
                    <td>{Number(o.amount || 0).toLocaleString()}</td>
                    <td className="plans-details">
                      {typeof o.data === "string" ? o.data : JSON.stringify(o.data)}
                    </td>
                    <td>
                      {o.createdAt
                        ? new Date(o.createdAt).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentLayer;
